// Rich SLA card for the ticket detail right-rail. For each goal (first response
// + resolution) it shows a live countdown (re-derived from `due_at` every 30s),
// the exact due date/time, and a progress bar that turns amber within the last
// hour and red once breached. Completed goals collapse to a Met / Missed state.
import React, { useEffect, useState } from 'react';
import { Clock, WarningCircle, CheckCircle } from '@phosphor-icons/react';

function fmtDuration(ms) {
  const totalMin = Math.round(Math.abs(ms) / 60000);
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const fmtWhen = (ms) =>
  new Date(ms).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

function SlaRow({ label, goal, now }) {
  if (!goal) return null;
  const testid = `sla-row-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const dueMs = goal.due_at ? new Date(goal.due_at).getTime() : null;

  // Goal already achieved (replied to / resolved): show Met or Missed.
  if (goal.completed) {
    const met = goal.met;
    return (
      <div data-testid={testid}>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-zinc-500 text-xs">{label}</span>
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${met ? 'text-emerald-600' : 'text-red-600'}`}>
            {met ? <CheckCircle size={13} weight="fill" /> : <WarningCircle size={13} weight="fill" />}
            {met ? 'Met' : 'Missed'}
          </span>
        </div>
        <div className={`h-1.5 rounded-full ${met ? 'bg-emerald-500' : 'bg-red-500'}`} />
        {dueMs && <p className="text-[10px] text-zinc-400 mt-1">Target: {fmtWhen(dueMs)}</p>}
      </div>
    );
  }

  // Open clock — derive remaining live from due_at.
  const remainingMs = dueMs != null ? dueMs - now : null;
  const breached = remainingMs != null && remainingMs < 0;
  const atRisk = !breached && remainingMs != null && remainingMs <= 60 * 60000;
  const target = goal.target_mins || null;
  const elapsedMin = target && remainingMs != null ? target - remainingMs / 60000 : null;
  const pct = target && elapsedMin != null
    ? Math.max(3, Math.min(100, (elapsedMin / target) * 100))
    : (breached ? 100 : 50);

  const tone = breached
    ? { txt: 'text-red-600', bar: 'bg-red-500' }
    : atRisk
      ? { txt: 'text-amber-600', bar: 'bg-amber-500' }
      : { txt: 'text-emerald-600', bar: 'bg-emerald-500' };
  const Icon = breached ? WarningCircle : Clock;

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-zinc-500 text-xs">{label}</span>
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${tone.txt}`} data-testid={`${testid}-status`}>
          <Icon size={13} weight="fill" />
          {remainingMs == null
            ? '—'
            : breached
              ? `Overdue by ${fmtDuration(remainingMs)}`
              : `Due in ${fmtDuration(remainingMs)}`}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
        <div className={`h-full rounded-full ${tone.bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      {dueMs && (
        <p className="text-[10px] text-zinc-400 mt-1">
          {breached ? 'Was due ' : 'Due by '}{fmtWhen(dueMs)}
        </p>
      )}
    </div>
  );
}

export default function SlaCard({ slaState }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  if (!slaState || (!slaState.response && !slaState.resolution)) return null;

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="sd-ticket-sla">
      <p className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium mb-3">SLA</p>
      <div className="space-y-3.5">
        <SlaRow label="First response" goal={slaState.response} now={now} />
        <SlaRow label="Resolution" goal={slaState.resolution} now={now} />
      </div>
    </div>
  );
}
